/* ===== Idle logout — force sign-out after inactivity for administrative sessions ===== */

import { useEffect, useRef } from 'react'
import { useApp } from '@/presentation/providers/useApp'

const ADMINISTRATIVE_ROLES = new Set([
  'organization_admin',
  'moderator',
  'manager',
  'treasurer',
  'financial_reviewer',
])

const IDLE_TIMEOUT_MS = Number(import.meta.env.VITE_IDLE_TIMEOUT_MS) || 30 * 60 * 1000
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const

export function useIdleLogout() {
  const { user, profile, currentMembership, signOut } = useApp()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isAdministrative =
    !!user &&
    (profile?.isSuperAdmin || (currentMembership?.roles ?? []).some((role) => ADMINISTRATIVE_ROLES.has(role)))

  useEffect(() => {
    if (!isAdministrative) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return undefined
    }

    const resetTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        void signOut()
      }, IDLE_TIMEOUT_MS)
    }

    resetTimer()
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer))

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer))
    }
  }, [isAdministrative, signOut])
}
